import type {
  DefaultServerService,
  ServersService,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import { WorkspaceLocalCacheService } from '@affine/core/modules/workspace-local-cache';
import {
  type CalendarDisplaySettings,
  decodeCalendarDisplaySettings,
  DEFAULT_CALENDAR_DISPLAY_SETTINGS,
} from '@blocksuite/data-view';
import { Service } from '@toeverything/infra';
import { distinctUntilChanged, map, switchMap } from 'rxjs';

const NAMESPACE = 'planner';
const SEGMENT = 'display-v1';
const LOCAL_ANONYMOUS = 'local-anonymous';

export type PlannerDisplaySettingsLoadResult =
  | { status: 'corrupt' | 'scope_changed' | 'unavailable' }
  | { settings: CalendarDisplaySettings; status: 'ready' };

/** Presentation-only request/response service. No bus: work is small, local and non-retryable. */
export class PlannerPresentationSettingsService extends Service {
  private readonly local = new Map<string, CalendarDisplaySettings>();
  private generation = 0;
  private localRealm: string | null = null;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly defaultServerService: DefaultServerService,
    private readonly serversService: ServersService,
    private readonly cache: WorkspaceLocalCacheService
  ) {
    super();
    const invalidate = () => {
      this.generation += 1;
      this.local.clear();
      this.localRealm = null;
    };
    const accountSubscription = this.workspaceServerService.server$
      .pipe(
        switchMap(
          server => (server ?? this.defaultServerService.server).account$
        ),
        distinctUntilChanged((previous, next) => previous?.id === next?.id)
      )
      .subscribe(invalidate);
    const scopeSubscription = this.cache.scope$.subscribe(invalidate);
    const serverSetSubscription = this.serversService.servers$
      .pipe(
        map(servers =>
          servers
            .map(server => server.id)
            .sort()
            .join('\u0000')
        ),
        distinctUntilChanged()
      )
      .subscribe(invalidate);
    this.disposables.push(() => scopeSubscription.unsubscribe());
    this.disposables.push(() => accountSubscription.unsubscribe());
    this.disposables.push(() => serverSetSubscription.unsubscribe());
  }

  async load(): Promise<PlannerDisplaySettingsLoadResult> {
    if (this.workspaceService.workspace.flavour === 'local') {
      this.syncLocalRealm();
      return {
        settings:
          this.local.get(this.localKey()) ?? DEFAULT_CALENDAR_DISPLAY_SETTINGS,
        status: 'ready',
      };
    }
    const generation = this.generation;
    const result = await this.cache.readResult<unknown>(NAMESPACE, SEGMENT);
    if (generation !== this.generation) return { status: 'scope_changed' };
    if (result.status === 'absent') {
      return { settings: DEFAULT_CALENDAR_DISPLAY_SETTINGS, status: 'ready' };
    }
    if (result.status === 'scope_changed') return { status: 'scope_changed' };
    if (result.status === 'read_error') return { status: 'unavailable' };
    const settings = decodeCalendarDisplaySettings(result.value);
    if (settings) return { settings, status: 'ready' };
    const removed = await this.cache.removeResult(NAMESPACE, SEGMENT);
    return removed.status === 'scope_changed'
      ? { status: 'scope_changed' }
      : { status: 'corrupt' };
  }

  async save(settings: CalendarDisplaySettings): Promise<boolean> {
    const canonical = decodeCalendarDisplaySettings(settings);
    if (!canonical) return false;
    if (this.workspaceService.workspace.flavour === 'local') {
      this.syncLocalRealm();
      this.local.set(this.localKey(), canonical);
      return true;
    }
    return this.cache.write(NAMESPACE, SEGMENT, canonical);
  }

  async reset(): Promise<PlannerDisplaySettingsLoadResult> {
    if (this.workspaceService.workspace.flavour === 'local') {
      this.syncLocalRealm();
      this.local.set(this.localKey(), DEFAULT_CALENDAR_DISPLAY_SETTINGS);
      return { settings: DEFAULT_CALENDAR_DISPLAY_SETTINGS, status: 'ready' };
    }
    const removed = await this.cache.removeResult(NAMESPACE, SEGMENT);
    if (removed.status === 'scope_changed') return { status: 'scope_changed' };
    if (removed.status === 'remove_error') return { status: 'unavailable' };
    return (await this.cache.write(
      NAMESPACE,
      SEGMENT,
      DEFAULT_CALENDAR_DISPLAY_SETTINGS
    ))
      ? { settings: DEFAULT_CALENDAR_DISPLAY_SETTINGS, status: 'ready' }
      : { status: 'unavailable' };
  }

  private localKey(): string {
    const accountId = this.currentAccountId();
    return `${this.workspaceService.workspace.id}:${accountId ?? LOCAL_ANONYMOUS}`;
  }

  private syncLocalRealm(): void {
    const realm = this.currentAccountId() ?? LOCAL_ANONYMOUS;
    if (this.localRealm !== null && this.localRealm !== realm)
      this.local.clear();
    if (this.localRealm !== realm) this.generation += 1;
    this.localRealm = realm;
  }

  private currentAccountId(): string | null {
    return (
      (this.workspaceServerService.server ?? this.defaultServerService.server)
        .account$.value?.id ?? null
    );
  }

  override dispose(): void {
    this.local.clear();
    this.localRealm = null;
    super.dispose();
  }
}
